import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { DECLINED } from '@rsos/constants/peerAgenciesStatuses';
import normalizeError from '@rsos/sinatra/src/utils/normalizeError';
import { groupBy } from '@rsos/utils/groupBy';
import {
  getAgencies,
  getAgencyLinks,
  postAgency,
  patchAgency,
  deleteAgency,
} from './alertsRoutingAPI';

const OUTBOUND = 'outbound';
const INBOUND = 'inbound';

const initialState = {
  agencyLinks: {
    error: null,
    linksFrom: [], // These are links from other agencies to mine, e.g. invites
    linksTo: [], // These are links to other agencies from mine, where I can share with them
    shareableAlertEmergencyTypes: [],
    loading: false,
  },
  error: null,
  loading: false,
};

export const fetchAgencyLinks = createAsyncThunk(
  'alertsRouting/fetchAgencyLinks',
  async (orgName, { rejectWithValue }) => {
    try {
      const response = await getAgencyLinks(orgName);
      return response.data;
    } catch (error) {
      const { message } = normalizeError(error);
      return rejectWithValue(message);
    }
  },
);

export const fetchAgencies = createAsyncThunk(
  'alertsRouting/fetchAgencies',
  async (data, { rejectWithValue }) => {
    try {
      const response = await getAgencies(data);
      return response.data;
    } catch (error) {
      const { message } = normalizeError(error);
      return rejectWithValue(message);
    }
  },
);

export const addAgency = createAsyncThunk(
  'alertsRouting/addAgencies',
  async (data, { rejectWithValue }) => {
    try {
      const response = await postAgency({
        link_type: 'alerts',
        org: data.org,
        link_to: data.link_to,
        alert_emergency_types: data.alert_emergency_types,
      });
      return response.data;
    } catch (error) {
      const { message } = normalizeError(error);
      return rejectWithValue(message);
    }
  },
);

export const updateAgency = createAsyncThunk(
  'alertsRouting/updateAgencies',
  async (data, { rejectWithValue }) => {
    try {
      const response = await patchAgency({
        org: data.org,
        id: data.id,
        status: data.status,
        alert_emergency_types: data.alert_emergency_types,
      });
      return response.data;
    } catch (error) {
      const { message } = normalizeError(error);
      return rejectWithValue(message);
    }
  },
);

export const removeAgency = createAsyncThunk(
  'alertsRouting/removeAgency',
  async (data, { rejectWithValue }) => {
    try {
      const response = await deleteAgency(data);
      return response.data;
    } catch (error) {
      const { message } = normalizeError(error);
      return rejectWithValue(message);
    }
  },
);

export const alertsRoutingSlice = createSlice({
  name: 'alertsRouting',
  initialState,
  reducers: {
    removeAgencyLinks: (state, action) => {
      const { id, type } = action.payload;
      if (type === OUTBOUND || type === INBOUND) {
        const linkType = type === OUTBOUND ? 'linksTo' : 'linksFrom';
        state.agencyLinks[linkType] = state.agencyLinks[linkType].filter(
          agency => agency.id !== id,
        );
      }
    },
    removeDeclinedAgencyLinksTo: (state, action) => {
      state.agencyLinks.linksTo = state.agencyLinks.linksTo.filter(
        agency =>
          agency.status !== DECLINED && agency.link_to.id !== action.payload,
      );
    },
    setAgencyLinks: (state, action) => {
      const { id, alert_emergency_types, status, type } = action.payload;
      if (type === OUTBOUND || type === INBOUND) {
        state.agencyLinks[type === OUTBOUND ? 'linksTo' : 'linksFrom'].filter(
          agency => {
            if (agency.id === id) {
              if (alert_emergency_types) {
                agency.attributes.alert_emergency_types = alert_emergency_types;
              }
              if (status) {
                agency.status = status;
              }
            }
          },
        );
      }
    },
    setPendingAgencyLinksTo: (state, action) => {
      // Context: In the `link to` list, there can only be one declined or pending
      // request from any agency. If there is already a declined request in the list, and the sharer
      // follows up with another pending request, the existing declined request should be deleted
      // and replaced by the new pending request. This is needed because the GET `agency-link`
      // request only happens on the dashboard page refresh.
      // The list of status is grouped by the following statuses then sort by name:
      // active/deactivated > pending > declined
      const pendingAgency = action.payload;
      let agenciesByStatus = groupBy([...state.agencyLinks.linksTo], 'status');
      let sortedAgencies = [];

      agenciesByStatus.pending = agenciesByStatus.pending
        ? [...agenciesByStatus.pending, pendingAgency]
        : [pendingAgency];
      agenciesByStatus.pending.sort((a, b) =>
        a.link_to.display_name
          .toLowerCase()
          .localeCompare(b.link_to.display_name.toLowerCase()),
      );
      for (const status in agenciesByStatus) {
        sortedAgencies = [...sortedAgencies, agenciesByStatus[status]];
      }
      state.agencyLinks.linksTo = sortedAgencies.flat();
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchAgencyLinks.pending, state => {
        state.agencyLinks.loading = true;
      })
      .addCase(fetchAgencyLinks.fulfilled, (state, action) => {
        const response = action.payload;
        state.agencyLinks.loading = false;
        state.agencyLinks.linksFrom = response?.links_from;
        state.agencyLinks.linksTo = response?.links_to;
        state.agencyLinks.shareableAlertEmergencyTypes =
          response?.shareable_alert_emergency_types;
      })
      .addCase(fetchAgencyLinks.rejected, (state, action) => {
        state.agencyLinks.loading = false;
        state.agencyLinks.error = action.payload;
      })
      .addCase(fetchAgencies.pending, state => {
        state.loading = true;
      })
      .addCase(fetchAgencies.fulfilled, state => {
        state.loading = false;
      })
      .addCase(fetchAgencies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addAgency.pending, state => {
        state.loading = true;
      })
      .addCase(addAgency.fulfilled, state => {
        state.loading = false;
      })
      .addCase(addAgency.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateAgency.pending, state => {
        state.loading = true;
      })
      .addCase(updateAgency.fulfilled, state => {
        state.loading = false;
      })
      .addCase(updateAgency.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(removeAgency.pending, state => {
        state.loading = true;
      })
      .addCase(removeAgency.fulfilled, state => {
        state.loading = false;
      })
      .addCase(removeAgency.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  removeAgencyLinks,
  removeDeclinedAgencyLinksTo,
  setAgencyLinks,
  setPendingAgencyLinksTo,
} = alertsRoutingSlice.actions;

export default alertsRoutingSlice.reducer;
